'use client';

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslationDetector } from '@/hooks/useTranslationDetector';
import { byPrefixAndName } from '@awesome.me/kit-12cf79935c/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Checkbox from '@/components/ui/input/Checkbox';
import Radio from '@/components/ui/input/Radio';
import { Button } from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import clsx from 'clsx';
import {
    useHotelLandingFilterDataLayer,
    FILTER_CATEGORY_MAP,
} from '@/components/pages/hotel/search/hooks/useHotelLandingFilterDataLayer';

// Amenity code to icon mapping
const amenityIconMap = {
    3: "circle-wifi",
    7: "pet-custom",
    8: "wheelchair-1",
    9: "circle-parking",
    11: "vault",
    13: "water-ladder-1-1",
    22: "child-hotel",
    23: "air-conditioner",
    27: "bus-service-1",
    28: "umbrella-beach-1",
    30: "hot-tub-person",
    39: "water-ladder-1-1",
    60: "water-ladder-1-1",
    85: "unlock-room",
    88: "cstm-smarttv",
    89: "custom-nespresso",
    107: "hotel-building-new",
    159: "person-swimming-child-1",
    186: "charging-station-1",
    247: "water",
    257: "spa-1",
    267: "shower",
    268: "shower",
    269: "bath",
    270: "hair-dryer",
    272: "mini-bar",
    292: "terrace-new",
    295: "leak-sea",
    296: "leak-sea",
    297: "tree",
    302: "airplane-otel",
    372: "mountbig",
    373: "blueflag",
    404: "tulip",
    563: "umbrella-beach-1",
    720: "circle-parking",
    721: "circle-parking",
    727: "circle-parking"
};

export default function LandingFilterModalContent({
    filters = {},
    onFilterChange = () => { },
    filterOptions = {},
    currentSortId = '0',
    onApply = () => { },
    onReset = () => { },
    onClose = () => { },
    initialScrollTo = null
}) {
    const t = useTranslationDetector('hotel');
    const { sendFilterAppliedEvent, getSortingType } =
        useHotelLandingFilterDataLayer();
    // Local state for all filters (initialized from props)
    const [localFilters, setLocalFilters] = useState(() => ({
        stars: filters.stars || [],
        chains: filters.chains || [],
        locations: filters.locations || [],
        themes: filters.themes || [],
        amenities: filters.amenities || [],
        rating: filters.rating || '',
    }));

    // "Daha fazla göster" state'leri
    const [showAllLocations, setShowAllLocations] = useState(false);
    const [showAllChains, setShowAllChains] = useState(false);
    const [showAllThemes, setShowAllThemes] = useState(false);
    const [showAllAmenities, setShowAllAmenities] = useState(false);

    // Filters prop değiştiğinde local state'i güncelle (modal yeniden açıldığında)
    useEffect(() => {
        setLocalFilters({
            stars: filters.stars || [],
            chains: filters.chains || [],
            locations: filters.locations || [],
            themes: filters.themes || [],
            amenities: filters.amenities || [],
            rating: filters.rating || '',
        });
    }, [filters]);

    // Aktif filtreleri hesapla (local state'ten)
    const activeFilters = useMemo(() => {
        const active = [];
        const { stars, chains, locations, themes, amenities, rating } = localFilters;

        if (stars && stars.length > 0) {
            stars.forEach((star) => {
                active.push({
                    type: 'star',
                    value: star,
                    label: t('starsFilter', { star }),
                    key: `star-${star}`,
                });
            });
        }

        if (locations && locations.length > 0) {
            locations.forEach((locationId) => {
                const location = filterOptions.locations?.find(l => l.Id === locationId);
                if (location) {
                    active.push({
                        type: 'location',
                        value: locationId,
                        label: location.Name,
                        key: `location-${locationId}`,
                    });
                }
            });
        }

        if (themes && themes.length > 0) {
            themes.forEach((themeId) => {
                const theme = filterOptions.themes?.find(t => t.Id === themeId);
                if (theme) {
                    active.push({
                        type: 'theme',
                        value: themeId,
                        label: theme.Name,
                        key: `theme-${themeId}`,
                    });
                }
            });
        }

        if (chains && chains.length > 0) {
            chains.forEach((chainSlug) => {
                const chain = filterOptions.chains?.find(c => c.Slug === chainSlug);
                active.push({
                    type: 'chain',
                    value: chainSlug,
                    label: chain?.Name || chainSlug,
                    key: `chain-${chainSlug}`,
                });
            });
        }

        if (amenities && amenities.length > 0) {
            amenities.forEach((amenityId) => {
                const amenity = filterOptions.amenities?.find(a => a.Id === amenityId);
                if (amenity) {
                    active.push({
                        type: 'amenity',
                        value: amenityId,
                        label: amenity.Name,
                        key: `amenity-${amenityId}`,
                    });
                }
            });
        }

        if (rating) {
            const ratingLabels = {
                '9+': `${t('Excellent')} 9+`,
                '8+': `${t('Great')} 8+`,
                '7+': `${t('VeryGood')} 7+`,
                '6+': `${t('Good')} 6+`,
            };
            active.push({
                type: 'rating',
                value: rating,
                label: ratingLabels[rating] || rating,
                key: 'rating',
            });
        }

        return active;
    }, [localFilters, filterOptions, t]);

    // Filtre kaldırma fonksiyonu (sadece local state'i güncelle)
    const removeFilter = (filterToRemove) => {
        setLocalFilters(prev => {
            const updated = { ...prev };

            switch (filterToRemove.type) {
                case 'star':
                    updated.stars = (updated.stars || []).filter(
                        (s) => s !== filterToRemove.value
                    );
                    break;
                case 'location':
                    updated.locations = (updated.locations || []).filter(
                        (l) => l !== filterToRemove.value
                    );
                    break;
                case 'theme':
                    updated.themes = (updated.themes || []).filter(
                        (t) => t !== filterToRemove.value
                    );
                    break;
                case 'chain':
                    updated.chains = (updated.chains || []).filter(
                        (c) => c !== filterToRemove.value
                    );
                    break;
                case 'amenity':
                    updated.amenities = (updated.amenities || []).filter(
                        (a) => a !== filterToRemove.value
                    );
                    break;
                case 'rating':
                    updated.rating = '';
                    break;
            }

            return updated;
        });
    };

    // Tüm filtreleri temizle (sadece local state'i güncelle)
    const clearAllFilters = () => {
        setLocalFilters({
            stars: [],
            chains: [],
            locations: [],
            themes: [],
            amenities: [],
            rating: '',
        });
    };

    // Handler functions (sadece local state'i güncelle)
    const handleStarChange = (starValue, checked) => {
        setLocalFilters(prev => {
            let newStars = [...(prev.stars || [])];
            if (checked) {
                if (!newStars.includes(starValue)) {
                    newStars.push(starValue);
                }
            } else {
                newStars = newStars.filter((s) => s !== starValue);
            }
            return { ...prev, stars: newStars };
        });
    };

    const handleLocationChange = (locationId, checked) => {
        setLocalFilters(prev => {
            let newLocations = [...(prev.locations || [])];
            if (checked) {
                if (!newLocations.includes(locationId)) {
                    newLocations.push(locationId);
                }
            } else {
                newLocations = newLocations.filter((l) => l !== locationId);
            }
            return { ...prev, locations: newLocations };
        });
    };

    const handleThemeChange = (themeId, checked) => {
        setLocalFilters(prev => {
            let newThemes = [...(prev.themes || [])];
            if (checked) {
                if (!newThemes.includes(themeId)) {
                    newThemes.push(themeId);
                }
            } else {
                newThemes = newThemes.filter((t) => t !== themeId);
            }
            return { ...prev, themes: newThemes };
        });
    };

    const handleChainChange = (chainSlug, checked) => {
        setLocalFilters(prev => {
            let newChains = [...(prev.chains || [])];
            if (checked) {
                if (!newChains.includes(chainSlug)) {
                    newChains.push(chainSlug);
                }
            } else {
                newChains = newChains.filter((c) => c !== chainSlug);
            }
            return { ...prev, chains: newChains };
        });
    };

    const handleAmenityChange = (amenityId, checked) => {
        setLocalFilters(prev => {
            let newAmenities = [...(prev.amenities || [])];
            if (checked) {
                if (!newAmenities.includes(amenityId)) {
                    newAmenities.push(amenityId);
                }
            } else {
                newAmenities = newAmenities.filter((a) => a !== amenityId);
            }
            return { ...prev, amenities: newAmenities };
        });
    };

    const handleRatingChange = (ratingValue) => {
        setLocalFilters(prev => ({ ...prev, rating: ratingValue }));
    };

    // Tab navigation
    const tabs = [
        { id: 'location', label: t('districtRegion') },
        { id: 'rating', label: t('guestRating') },
        { id: 'stars', label: t('hotelStars') },
        { id: 'chains', label: t('chains') },
        { id: 'themes', label: t('themes') },
        { id: 'amenities', label: t('facilityFeatures') },
    ];

    const [activeTab, setActiveTab] = useState('location');
    const contentRef = useRef(null);
    const tabsContainerRef = useRef(null);
    const isProgrammaticScrollRef = useRef(false);

    // Scroll to section (başlık görünür kalacak şekilde)
    const scrollToSection = useCallback((sectionId) => {
        isProgrammaticScrollRef.current = true;
        setTimeout(() => {
            const element = document.getElementById(sectionId);
            if (element && contentRef.current) {
                const container = contentRef.current;
                // Sticky tab bar altında section başlığı görünür kalsın
                const HEADER_OFFSET = 140;
                let elementTop = element.offsetTop - HEADER_OFFSET;
                if (elementTop < 0) elementTop = 0;
                container.scrollTo({
                    top: elementTop,
                    behavior: 'smooth'
                });
            }
            setTimeout(() => {
                isProgrammaticScrollRef.current = false;
            }, 450);
        }, 100);
    }, []);

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        scrollToSection(`section-${tabId}`);
    };

    // Initial scroll when modal opens with initialScrollTo prop
    useEffect(() => {
        if (initialScrollTo) {
            const tabId = initialScrollTo;
            setActiveTab(tabId);
            // Wait a bit for modal to fully render
            setTimeout(() => {
                scrollToSection(`section-${tabId}`);
            }, 300);
        }
    }, [initialScrollTo, scrollToSection]);

    // Scroll-spy: scroll pozisyonuna göre aktif tabı güncelle
    useEffect(() => {
        const container = contentRef.current;
        if (!container || !tabs.length) return;

        const sectionIds = tabs.map(t => t.id);
        const REFERENCE_OFFSET = 150;
        const THRESHOLD = 30;

        const updateActiveTab = () => {
            if (isProgrammaticScrollRef.current) return;

            const scrollTop = container.scrollTop;
            const scrollBottom = container.scrollHeight - container.clientHeight;

            if (scrollTop <= THRESHOLD) {
                setActiveTab(sectionIds[0]);
                return;
            }
            if (scrollTop >= scrollBottom - THRESHOLD) {
                setActiveTab(sectionIds[sectionIds.length - 1]);
                return;
            }

            const referenceLine = scrollTop + REFERENCE_OFFSET;
            let nextActiveId = sectionIds[0];
            for (let i = 0; i < sectionIds.length; i++) {
                const el = document.getElementById(`section-${sectionIds[i]}`);
                if (el && el.offsetTop <= referenceLine) {
                    nextActiveId = sectionIds[i];
                }
            }
            setActiveTab(nextActiveId);
        };

        container.addEventListener('scroll', updateActiveTab, { passive: true });
        updateActiveTab();
        return () => container.removeEventListener('scroll', updateActiveTab);
    }, [tabs]);

    // Aktif tab görünür değilse yatay listede merkeze kaydır
    useEffect(() => {
        if (!tabsContainerRef.current || !activeTab) return;
        const container = tabsContainerRef.current;
        const activeButton = container.querySelector(
            `button[data-id="${activeTab}"]`,
        );
        if (!activeButton) return;

        const containerRect = container.getBoundingClientRect();
        const activeRect = activeButton.getBoundingClientRect();

        const isFullyVisible =
            activeRect.left >= containerRect.left &&
            activeRect.right <= containerRect.right;

        if (!isFullyVisible) {
            activeButton.scrollIntoView({
                behavior: 'smooth',
                inline: 'center',
                block: 'nearest',
            });
        }
    }, [activeTab]);

    // Statik seçenekler
    const starOptions = [5, 4, 3, 2, 1, 0];
    const ratingOptions = [
        { value: '9+', label: t('Excellent'), labelPoint: '9+' },
        { value: '8+', label: t('Great'), labelPoint: '8+' },
        { value: '7+', label: t('VeryGood'), labelPoint: '7+' },
        { value: '6+', label: t('Good'), labelPoint: '6+' },
    ];

    return (
        <div className="flex flex-col h-full min-h-0">
            {/* Tab Navigation - Sticky */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                <div
                    ref={tabsContainerRef}
                    className="flex overflow-x-scroll scrollbar-hide"
                >
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            data-id={tab.id}
                            onClick={() => handleTabClick(tab.id)}
                            className={clsx(
                                'px-4 py-3 text-sm font-medium whitespace-nowrap shrink-0 border-b-2 transition-colors',
                                activeTab === tab.id
                                    ? 'text-primary border-primary'
                                    : 'text-foreground border-transparent hover:text-primary'
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Content - Scrollable */}
            <div
                ref={contentRef}
                className="flex-1 min-h-0 overflow-y-auto bg-background-global"
            >
                <div className="p-4 space-y-6 pb-24">
                    {/* İlçe / Bölge */}
                    {filterOptions.locations && filterOptions.locations.length > 0 && (
                        <section id="section-location">
                            <h3 className="text-base font-semibold text-foreground mb-3">
                                {t('districtRegion')}
                            </h3>
                            <div className="flex flex-col bg-white px-4 rounded-lg">
                                {(showAllLocations ? filterOptions.locations : filterOptions.locations.slice(0, 5)).map((location) => (
                                    <div className='py-4 border-b border-[#E8E8E9]' key={location.Id}>
                                        <Checkbox
                                            checked={localFilters.locations?.includes(location.Id) || false}
                                            onChange={(e) => handleLocationChange(location.Id, e.target.checked)}
                                            className="flex-row-reverse md:flex-row"
                                        >
                                            <span className="text-sm font-normal text-foreground">{location.Name}</span>
                                        </Checkbox>
                                    </div>
                                ))}
                                {filterOptions.locations.length > 5 && (
                                    <button
                                        onClick={() => setShowAllLocations(!showAllLocations)}
                                        className="text-sm font-medium text-foreground text-left cursor-pointer py-[14px] py-4"
                                    >
                                        {showAllLocations ? t('showLess') : t('showMore')}
                                    </button>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Misafir Puanı */}
                    <section id="section-rating">
                        <h3 className="text-base font-semibold text-foreground mb-3">
                            {t('guestRating')}
                        </h3>
                        <div className="flex flex-col bg-white px-4 rounded-lg">
                            {ratingOptions.map((ratingOption) => (
                                <div className='py-4 border-b border-[#E8E8E9]' key={ratingOption.value}>
                                    <Radio
                                        name="rating"
                                        checked={localFilters.rating === ratingOption.value}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                handleRatingChange(ratingOption.value);
                                            }
                                        }}
                                        className="flex-row-reverse md:flex-row"
                                    >
                                        <div className="flex items-center gap-1">
                                            <span className="text-sm font-normal text-foreground">{ratingOption.label}</span>
                                            <span className="text-sm text-foreground font-medium">{ratingOption.labelPoint}</span>
                                        </div>
                                    </Radio>
                                </div>
                            ))}
                            <Radio
                                name="rating"
                                checked={localFilters.rating === 'unrated'}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        handleRatingChange('unrated');
                                    }
                                }}
                                className="flex-row-reverse md:flex-row py-4"
                            >
                                <span className="text-sm font-normal text-foreground">{t('unrated')}</span>
                            </Radio>
                        </div>
                    </section>

                    {/* Otel Yıldızları */}
                    <section id="section-stars">
                        <h3 className="text-base font-semibold text-foreground mb-3">
                            {t('hotelStars')}
                        </h3>
                        <div className="flex flex-col bg-white px-4 rounded-lg">
                            {starOptions.map((star) => (
                                <div className='py-4 border-b border-[#E8E8E9]' key={star}>
                                    <Checkbox
                                        checked={localFilters.stars?.includes(star) || false}
                                        onChange={(e) => handleStarChange(star, e.target.checked)}
                                        className="flex-row-reverse md:flex-row"
                                    >
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-normal text-foreground">
                                                {star === 0 ? t('unrated') : t('starsFilter', { star })}
                                            </span>
                                            {star > 0 && (
                                                <div className="flex items-center gap-[1px]">
                                                    {Array.from({ length: star }).map((_, index) => (
                                                        <span key={index} className='!w-3.5 !h-3.5 flex items-center justify-center'>
                                                            <FontAwesomeIcon
                                                                key={index}
                                                                icon={byPrefixAndName.fas['star']}
                                                                className="text-yellow !w-[12px] !h-[11px]"
                                                            />
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </Checkbox>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Zincirler */}
                    {filterOptions.chains && filterOptions.chains.length > 0 && (
                        <section id="section-chains">
                            <h3 className="text-base font-semibold text-foreground mb-3">
                                {t('chains')}
                            </h3>
                            <div className="flex flex-col bg-white px-4 rounded-lg">
                                {(showAllChains ? filterOptions.chains : filterOptions.chains.slice(0, 5)).map((chain) => (
                                    <div className='py-4 border-b border-[#E8E8E9] last:border-b-0' key={chain.Slug}>
                                        <Checkbox
                                            checked={localFilters.chains?.includes(chain.Slug) || false}
                                            onChange={(e) => handleChainChange(chain.Slug, e.target.checked)}
                                            className="flex-row-reverse md:flex-row"
                                        >
                                            <span className="text-sm font-normal text-foreground">{chain.Name}</span>
                                        </Checkbox>
                                    </div>
                                ))}
                                {filterOptions.chains.length > 5 && (
                                    <button
                                        onClick={() => setShowAllChains(!showAllChains)}
                                        className="text-sm font-medium text-foreground text-left cursor-pointer py-4"
                                    >
                                        {showAllChains ? t('showLess') : t('showMore')}
                                    </button>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Temalar */}
                    {filterOptions.themes && filterOptions.themes.length > 0 && (
                        <section id="section-themes">
                            <h3 className="text-base font-semibold text-foreground mb-3">
                                {t('themes')}
                            </h3>
                            <div className="flex flex-col bg-white px-4 rounded-lg">
                                {(showAllThemes ? filterOptions.themes : filterOptions.themes.slice(0, 5)).map((theme) => (
                                    <div className='py-4 border-b border-[#E8E8E9]' key={theme.Id}>
                                        <Checkbox
                                            checked={localFilters.themes?.includes(theme.Id) || false}
                                            onChange={(e) => handleThemeChange(theme.Id, e.target.checked)}
                                            className="flex-row-reverse md:flex-row"
                                        >
                                            <span className="text-sm font-normal text-foreground">{theme.Name}</span>
                                        </Checkbox>
                                    </div>
                                ))}
                                {filterOptions.themes.length > 5 && (
                                    <button
                                        onClick={() => setShowAllThemes(!showAllThemes)}
                                        className="text-sm font-medium text-foreground text-left cursor-pointer py-4"
                                    >
                                        {showAllThemes ? t('showLess') : t('showMore')}
                                    </button>
                                )}
                            </div>
                        </section>
                    )}

                    {/* Tesis Özellikleri */}
                    {filterOptions.amenities && filterOptions.amenities.length > 0 && (
                        <section id="section-amenities">
                            <h3 className="text-base font-semibold text-foreground mb-3">
                                {t('facilityFeatures')}
                            </h3>
                            <div className="grid grid-cols-2 gap-x-[14px] gap-y-4">
                                {(showAllAmenities ? filterOptions.amenities : filterOptions.amenities.slice(0, 8)).map((amenity) => {
                                    const isChecked = localFilters.amenities?.includes(amenity.Id) || false;
                                    const amenityCode = amenity.Code || amenity.AmenityId || amenity.Id;
                                    const iconName = amenityIconMap[amenityCode] || "home";
                                    // Öncelik sırası: fak (custom) -> fas (FontAwesome solid) -> fallback home
                                    const icon = byPrefixAndName.fak[iconName] || byPrefixAndName.fal[iconName] || byPrefixAndName.fal["home"];

                                    return (
                                        <button
                                            key={amenity.Id}
                                            type="button"
                                            onClick={() => handleAmenityChange(amenity.Id, !isChecked)}
                                            className={clsx(
                                                "flex flex-col items-center justify-center gap-[6px] px-3 py-[10px] rounded-lg border transition-all duration-200 cursor-pointer",
                                                "hover:border-primary hover:bg-primary-weaker",
                                                isChecked
                                                    ? "border-primary bg-primary-weaker"
                                                    : "border-border-strong bg-white"
                                            )}
                                        >
                                            <FontAwesomeIcon
                                                icon={icon}
                                                className={clsx(
                                                    "text-xl",
                                                    isChecked ? "text-primary" : "text-foreground"
                                                )}
                                            />
                                            <span className={clsx(
                                                "text-sm font-medium text-center",
                                                isChecked ? "text-primary" : "text-foreground"
                                            )}>
                                                {amenity.Name}
                                            </span>
                                        </button>
                                    );
                                })}
                                {filterOptions.amenities.length > 8 && (
                                    <button
                                        onClick={() => setShowAllAmenities(!showAllAmenities)}
                                        className="text-sm font-medium text-foreground text-left cursor-pointer col-span-2"
                                    >
                                        {showAllAmenities ? t('showLess') : t('showMore')}
                                    </button>
                                )}
                            </div>
                        </section>
                    )}
                </div>
            </div>

            {/* Footer - Fixed in layout (Safari sticky-safe) */}
            <div
                className="flex-shrink-0 bg-white border-t border-gray-200 p-4 space-y-4 pr-0"
                style={{ paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))" }}
            >
                {/* Active Filters Badges */}
                {activeFilters.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                        {activeFilters.map((filter) => (
                            <Badge
                                key={filter.key}
                                variant="secondary"
                                size="sm"
                                className="group flex items-center gap-2 cursor-pointer py-2 pl-[10px] pr-2"
                            >
                                <span className='text-sm font-normal text-primary'>{filter.label}</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        removeFilter(filter);
                                    }}
                                    className="ml-1 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
                                    aria-label={t('removeFilter')}
                                >
                                    <FontAwesomeIcon
                                        icon={byPrefixAndName.far['xmark']}
                                        className="h-4 w-4 text-primary cursor-pointer"
                                    />
                                </button>
                            </Badge>
                        ))}
                    </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pr-4">
                    <Button
                        variant="outline"
                        onClick={clearAllFilters}
                        className="flex-1 h-[50px]"
                    >
                        {t('reset')}
                    </Button>
                    <Button
                        color="primary"
                        variant="solid"
                        onClick={() => {
                            const sortingType = getSortingType(currentSortId);
                            activeFilters.forEach((filter) => {
                                const category = FILTER_CATEGORY_MAP[filter.type];
                                if (category) {
                                    sendFilterAppliedEvent(
                                        category,
                                        filter.label,
                                        sortingType
                                    );
                                }
                            });
                            onFilterChange(localFilters);
                            onClose();
                        }}
                        className="flex-2 h-[50px]"
                    >
                        {t('applyFilters')}
                    </Button>
                </div>
            </div>
        </div>
    );
}

